// ── CompactLinesTable — shared compact spread/total chip-strip table ──
//
// The SpreadsTotals `EventRows` renderer wrapped in the GameDetail section
// table (10-col thead, MatchupRow suppressed). Used by BOTH
// SpreadTotalSection (its Compact view) and GameLinesSection (its Compact
// view on the spread/total tabs), so the compact presentation is built once.
//
// #882 — this wrapper is also where the Game Detail derivative book gets its
// own depth and geometry (`GAME_DETAIL_COMPACT_LADDER`: five levels per
// block, columns packed to the shared center). Pinned here rather than in
// each host so the two Game Detail sections cannot drift apart, while the
// Value Dashboard's SpreadsTotals page — which renders `SpreadsTotalsTable`
// directly — keeps the shared three-level default untouched.
//
// Fully CONTROLLED: the parent owns line-selection + expand state and the
// trade/panel wiring (each section drives its own right-panel differently).
// This component is presentation only.

import { UsMetricHeaders } from '../ValueDashboard/us/UsMarketMetrics';
import { useEffect, useState } from 'react';
import { Tooltip } from '@/components/atoms/Tooltip';
import { EdgeFadeScroller } from '@/components/cards/cardKit';
import { useLt2Access } from '@/hooks/useLt2Access';
import { useDisplaySettings } from '@/stores/displaySettings';
import { EventRows, type EventRowsProps } from '../SpreadsTotals/SpreadsTotalsTable';
import { formatSignedLine } from '../SpreadsTotals/synthesize';
import { GAME_DETAIL_COMPACT_LADDER } from '../SpreadsTotals/TwoSidedLadder';
import type { SpreadLine, TotalLine } from '../ValueDashboard/types';

/** Everything EventRows needs except the fixed `eventIndex` / `hideMatchupRow`
 *  / `ladderPresentation`, which this wrapper pins. `showOpColumn` is also
 *  owned here (the explicit OP display preference) so both hosting
 *  sections stay untouched. */
export type CompactLinesTableProps = Omit<
  EventRowsProps,
  'eventIndex' | 'hideMatchupRow' | 'showOpColumn' | 'ladderPresentation'
>;

/**
 * Game Detail's wide tables scroll inside the shared EdgeFadeScroller, so the
 * columns hidden off the right at 1470 (the whole Poly half of a compact
 * Spread/Total table) are signalled by an edge fade and an always-visible bar.
 * Two adjustments for a card, both scoped to the scroller's own children:
 *  • the fades take the CARD colour (they are drawn in the page colour);
 *  • the bar's height is reserved while there is nothing to scroll, so the
 *    bar arriving or leaving (a resize, OP on/off, a long price) never moves
 *    the sections below.
 */
export const GAME_DETAIL_TABLE_FADE =
  '[&>[aria-hidden=true]]:[--background:var(--card)] [&:not(:has(>div>[role=scrollbar]))]:pb-2.5';

export function CompactLinesTable(props: CompactLinesTableProps) {
  // Source withdrawal must not remove a column and move executable prices.
  // Missing exact references remain unavailable; only the OP control hides it.
  const butterflyAccess = useLt2Access();
  const butterflyOn = useDisplaySettings((s) => s.spreadTotalNearestLines) && butterflyAccess;
  const showOpColumn = useDisplaySettings((s) => s.showOpOdds);
  return (
    <EdgeFadeScroller className={GAME_DETAIL_TABLE_FADE}>
      <table className="w-full border-collapse border-spacing-0">
        <thead className="bg-muted/50 border-border border-b">
          <tr className="text-muted-foreground text-left text-[10px] font-semibold uppercase tracking-wider">
            <th className="w-24 px-3 py-2"></th>
            <th className="w-72 px-3 py-2">Side &amp; Line</th>
            {showOpColumn && (
              <th
                className="w-24 px-3 py-2 text-center"
                title="OddsPapi cross-book de-vigged fair value at the selected line (hydracore feed)."
              >OP/PNCL</th>
            )}
            <th className="w-32 px-3 py-2 text-center">Kalshi</th>
            <th className="w-16 px-3 py-2 text-right" title="Cumulative traded contracts">K Vol</th>
            <th className="w-24 px-3 py-2 text-right" title="Total $ resting in book">K Liq</th>
            <th className="w-16 px-3 py-2 text-right" title="Kalshi best-bid depth change over the last 1m">K Δ/m</th>
            {!butterflyOn && <>
            <th className="w-32 px-3 py-2 text-center">Poly</th>
            <th className="w-16 px-3 py-2 text-right" title="Cumulative traded contracts">P Vol</th>
            <th className="w-24 px-3 py-2 text-right" title="Total $ resting in book">P Liq</th>
            <th className="w-16 px-3 py-2 text-right" title="Polymarket best-bid depth change over the last 1m">P Δ/m</th>
            {props.showUsColumn && (<>
              <th className="w-32 px-3 py-2 text-center" title="Polymarket US at this row's exact line: maker shows the best bid, taker the best ask. Empty when US lists no market at the line; greyed when its quote is not current.">US</th>
              <UsMetricHeaders compact />
            </>)}
            </>}
          </tr>
        </thead>
        <tbody>
          <EventRows
            {...props}
            eventIndex={0}
            hideMatchupRow
            showOpColumn={showOpColumn}
            ladderPresentation={GAME_DETAIL_COMPACT_LADDER}
          />
        </tbody>
      </table>
    </EdgeFadeScroller>
  );
}

/** Index of the line whose Kalshi YES price sits closest to 50¢ — the
 *  default-selected chip in each compact strip. Returns 0 for an empty list. */
export function closestKalshiYesTo50(centsList: Array<number | null | undefined>): number {
  if (centsList.length === 0) return 0;
  let bestIdx = 0;
  let bestDiff = Infinity;
  for (let i = 0; i < centsList.length; i++) {
    const cents = centsList[i];
    if (cents == null) continue;
    const diff = Math.abs(cents - 50);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestIdx = i;
    }
  }
  return bestIdx;
}

// ── The compact strip's line status, shown in the hosting section's header ──
//
// Two things about the line a compact strip shows have to be visible:
//  • whether it is HELD. An untouched strip follows the line nearest 50¢ and
//    can move to another line on any tick (Spread / Total with #1071's
//    initial-line switch on instead settles once and stays); touching a line (picking a chip,
//    expanding or inspecting the row, clicking its book, aiming it) holds it,
//    so an armed click can never land on a line the trader did not touch.
//  • that a picked line is NOT on the board, so the stand-in is never silent.
// A note row inserted above the table pushed every row and ladder down under
// the trader's pointer (#1087 review). The status lives in a line that is
// always there instead: it takes only the header's free width, never wraps
// and never changes the header's height, so nothing below it moves.

/** How long the line list must hold still before a missing pick is called
 *  "not listed". Lines arrive in bursts while a game hydrates; a pick that is
 *  only late must not be reported gone. */
export const LINE_LIST_SETTLE_MS = 1500;

/** True once `active` has held, with `signature` unchanged, for `ms`. Any
 *  change to the signature (a line arriving or leaving) restarts the wait. */
export function useLineListSettled(active: boolean, signature: string, ms = LINE_LIST_SETTLE_MS): boolean {
  const [settled, setSettled] = useState<string | null>(null);
  useEffect(() => {
    if (!active) return;
    const timer = window.setTimeout(() => setSettled(signature), ms);
    return () => window.clearTimeout(timer);
  }, [active, signature, ms]);
  return active && settled === signature;
}

/** A spread line as a trader names it away from the strip: the away team,
 *  then its signed line ("DAL +2.5"). */
export function spreadLineLabel(line: SpreadLine, awayCode: string): string {
  return [awayCode, formatSignedLine(line.line)].filter(Boolean).join(' ');
}

export function totalLineLabel(line: TotalLine): string {
  return line.line.toString();
}

export type LineHoldKind = 'spread' | 'total';
export type LineHoldState = 'held' | 'finding' | 'missing';

export interface LineHoldEntry {
  kind: LineHoldKind;
  /** The line as `spreadLineLabel` / `totalLineLabel` names it ('' if unknown). */
  label: string;
  /** held: the strip stays on this line while prices move.
   *  finding: the picked line is not on the board yet and the list is still
   *  changing. missing: the list has settled without it. */
  state: LineHoldState;
}

const KIND_WORD: Record<LineHoldKind, string> = { spread: 'Spread', total: 'Total' };

function lineName(entry: LineHoldEntry): string {
  return [KIND_WORD[entry.kind], entry.label].filter(Boolean).join(' ');
}

function noticeText(entry: LineHoldEntry): string {
  return entry.state === 'missing'
    ? `${lineName(entry)} not listed`
    : `Finding ${[entry.kind, entry.label].filter(Boolean).join(' ')}…`;
}

function noticeHelp(entry: LineHoldEntry): string {
  const picked = `The ${entry.kind} line you picked${entry.label ? ` (${entry.label})` : ''}`;
  return entry.state === 'missing'
    ? `${picked} isn’t listed right now. The strip shows the line nearest 50¢ until you touch one.`
    : `${picked} isn’t on the board yet. The strip shows the line nearest 50¢ while lines load.`;
}

function heldHelp(held: readonly LineHoldEntry[]): string {
  const names = held.map(lineName).join(' and ');
  return held.length > 1
    ? `${names} are held: they stay selected while prices move. Pick another chip to change one.`
    : `${names} is held: it stays selected while prices move. Pick another chip to change it.`;
}

const FOLLOWING_HELP =
  'An untouched strip follows the line nearest 50¢ as prices move. Picking a chip, opening or '
  + 'inspecting a row, or clicking its book holds that line.';

/** #1071, switched on: an untouched Spread / Total strip settles once (the
 *  board's line for the game; else, once priced, the busiest window line, or
 *  the 50¢ line when the rule cannot name one — nothing traded, the busiest
 *  line unpriced, no line it could check, or volume still unknown at the
 *  limit) and then stays. */
const SETTLED_HELP =
  'An untouched strip starts on the line the board shows for this game. Otherwise, once prices '
  + 'arrive, it settles within a few seconds on the most traded of the lines around 50¢, or on the '
  + '50¢ line when that cannot be told. It then stays on that line as prices move. Picking a '
  + 'chip, opening or inspecting a row, or clicking its book holds a line.';

/**
 * The section header's line status. Always renders its slot (an empty,
 * zero-basis flex item), so what it says never changes how the header's own
 * items lay out. Text truncates to the free width; the Tooltip carries the
 * full sentences. Notices about a missing pick come first and are the live
 * region; the held line follows.
 */
export function LineHoldStatus({ entries, untouched = 'follows' }: {
  entries: readonly LineHoldEntry[];
  /** What an untouched strip does: follows 50¢ (default), or settles once and
   *  stays (Spread / Total with #1071's initial-line switch on). */
  untouched?: 'follows' | 'settles';
}) {
  const notices = entries.filter((e) => e.state !== 'held');
  const held = entries.filter((e) => e.state === 'held');
  return (
    <div data-line-hold-status="" className="flex min-w-0 flex-1 items-center text-[11px] leading-4">
      {entries.length > 0 && (
        <Tooltip
          content={[
            ...notices.map(noticeHelp),
            ...(held.length > 0 ? [heldHelp(held)] : []),
            ...(held.length < 2 ? [untouched === 'settles' ? SETTLED_HELP : FOLLOWING_HELP] : []),
          ].map((line) => <p key={line}>{line}</p>)}
          position="bottom"
          className="w-80 space-y-1 whitespace-normal font-normal"
          wrapperClassName="relative flex min-w-0"
        >
          <span className="flex min-w-0 items-center gap-2 whitespace-nowrap">
            {notices.length > 0 && (
              <span role="status" className="min-w-0 shrink truncate">
                {notices.map((e, i) => (
                  <span
                    key={e.kind}
                    className={
                      e.state === 'missing'
                        ? 'font-medium text-amber-400 light:text-amber-700'
                        : 'text-muted-foreground'
                    }
                  >
                    {i > 0 ? ' · ' : ''}
                    {noticeText(e)}
                  </span>
                ))}
              </span>
            )}
            {held.length > 0 && (
              <span className="text-foreground/70 min-w-0 shrink-[2] truncate" data-line-held="">
                Held: {held.map(lineName).join(' · ')}
              </span>
            )}
          </span>
        </Tooltip>
      )}
    </div>
  );
}
